import "./ScanSettings.styles.css";
import Button from "@mui/material/Button";
import React, { useEffect, useMemo, useState } from "react";
import Stack from "@mui/material/Stack";
import {
    Drawer,
    FormControlLabel,
    FormGroup,
    FormHelperText,
    Switch, SxProps,
    TextField,
} from "@mui/material";
import { PageHeader } from "../../components/organisms/PageHeader";
import {
    ScanSettingsProps,
    IScanSettings,
    defaultScanSettings,
    ScanClassification,
    MASKED_KEY_SYMBOL,
} from "./ScanSettings.props";
import {
    buildChangedScanSettingsPayload,
    isEmptyObject,
    mergeChangedSettings,
} from "./ScanSettings.utils";
import LeavePageDialog from "../../components/organisms/LeavePageDialog/LeavePageDialog";
import { useCallbackPrompt } from "../../hooks/useCallbackPrompt";
import { Toast } from "../../components/atoms/Toast";
import Box from "@mui/material/Box";
import ChangeWithZone from "../../hoc/ChangeWithZone";
import { useTranslation } from "react-i18next";
import { SettingBox } from "../../components/molecules/SettingBox";
import InputLabel from "@mui/material/InputLabel";
import Skeleton from "@mui/material/Skeleton";
import Tooltip from "@mui/material/Tooltip";
import IconButton from "@mui/material/IconButton";
import HelpIcon from "@mui/icons-material/Help";
import Select, { SelectChangeEvent } from "@mui/material/Select";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import MenuItem from "@mui/material/MenuItem";
import CheckIcon from "@mui/icons-material/Check";
import Divider from "@mui/material/Divider";
import { getSettings, updateSetting } from "../../api/emailTriage";
import { UPDATE_SETTINGS_TASKS } from "../../constants/contants";
import { LoadingPage } from "../../components/organisms/LoadingPage";
import { styled } from "@mui/material/styles";

const GreenSwitch = styled(Switch)(() => ({
    "& .MuiSwitch-switchBase.Mui-checked": {
        color: "#008545",
        "&:hover": {
            backgroundColor: "#B3DAC7",
        },
    },
    "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
        backgroundColor: "#B3DAC7",
    },
}));

export const categories: { label: string; value: ScanClassification }[] = [
    { label: "Threat", value: "threat" },
    { label: "No Threat", value: "no_threat" },
    { label: "Undetermined", value: "undetermined" },
];

const isValidToken = (input: string): boolean => /^[A-Za-z0-9]{64}$/.test(input)

const ScanSettingFull: React.FC<ScanSettingsProps> = () => {
    const { t } = useTranslation();

    const [isLoading, setIsLoading] = useState(false);

    const [initialData, setInitialData] = useState<IScanSettings>(defaultScanSettings);
    const [scanSettingData, setSettingData] = useState<IScanSettings>(defaultScanSettings);
    const [inputErrorMsg, setInputErrorMsg] = useState<string>("");
    const [maskedKey, setMaskedKey] = useState<string>(MASKED_KEY_SYMBOL);

    const [isActionShow, setActionShow] = useState(false);
    const [isLoadingFullPage, setIsLoadingFullPage] = useState(false);

    const [vtKeyInputValue, setVtKeyInputValue] = useState("");
    const [isReplacingVtKey, setIsReplacingVtKey] = useState(false);
    const [isVtKeyFocused, setIsVtKeyFocused] = useState(false);
    const [hasVtKeyEditIntent, setHasVtKeyEditIntent] = useState(false);

    const [toastValue, setToastValue] = useState({
        type: "",
        message: "",
        isOpen: false,
        toastId: 0,
    });

    const hasExistingVtKey = !!initialData.vt_key;
    const isUsingStoredVtKey =
        hasExistingVtKey &&
        !isReplacingVtKey &&
        scanSettingData.vt_key === initialData.vt_key;

    const hasChanges = useMemo(() => {
        return (
            JSON.stringify(scanSettingData) !== JSON.stringify(initialData) ||
            hasVtKeyEditIntent
        );
    }, [scanSettingData, initialData, hasVtKeyEditIntent]);

    const [showPrompt, confirmNavigation, cancelNavigation] =
        useCallbackPrompt(hasChanges) as [boolean, () => void, () => void];

    const tooltipEl = (
        title: string,
        tooltipStyle: SxProps = {}
    ) => (
        <Tooltip
            placement={"top"}
            title={title}
            componentsProps={{
                 tooltip: {
                     sx: {
                         ...{
                            maxWidth: "450px",
                            width: "auto",
                            fontSize: "13px"
                        },
                        ...tooltipStyle
                     }
                },
            }}
        >
            <IconButton>
                <HelpIcon
                    color="primary"
                    sx={{ fontSize: "16px" }}
                />
            </IconButton>
        </Tooltip>
    )

    useEffect(() => {
        setIsLoading(true);

        getSettings({ task: "get_scan_settings" })
            .then((response) => {
                const data = response?.data ?? response ?? {};

                const nextData: IScanSettings = {
                    no_result: data?.no_result ?? defaultScanSettings.no_result,
                    on_error: data?.on_error ?? defaultScanSettings.on_error,
                    vt_on: data?.vt_on ?? defaultScanSettings.vt_on,
                    vt_key: data?.vt_key ?? defaultScanSettings.vt_key,
                };

                setInitialData(nextData);
                setSettingData(nextData);

                if (nextData.vt_key) {
                    setMaskedKey("*".repeat(String(nextData.vt_key).length));
                }

                setVtKeyInputValue(nextData.vt_key ? "" : nextData.vt_key);
                setIsReplacingVtKey(false);
                setIsVtKeyFocused(false);
                setHasVtKeyEditIntent(false);
                setActionShow(false);
            })
            .catch(() => {
                setToastValue({
                    type: "error",
                    message: "Failed to load settings",
                    isOpen: true,
                    toastId: Date.now(),
                });
            })
            .finally(() => {
                setIsLoading(false);
            });
    }, []);

    const updateChangedState = (
        nextData: IScanSettings,
        hasPendingVtKeyEdit = hasVtKeyEditIntent
    ) => {
        setSettingData(nextData);
        const changed =
            JSON.stringify(nextData) !== JSON.stringify(initialData) ||
            hasPendingVtKeyEdit;
        setActionShow(changed);
    };

    const resetVtKeyInputState = (data: IScanSettings) => {
        setVtKeyInputValue(data.vt_key ? "" : data.vt_key);
        setIsReplacingVtKey(false);
    };

    const handleSaveChange = () => {
        if (
            scanSettingData.vt_on &&
            isReplacingVtKey &&
            !vtKeyInputValue.trim()
        ) {
            setInputErrorMsg(t("pages.scanSettings.scanEngine.api_required"));
            setActionShow(true);
            return;
        }

        const changedPayload = buildChangedScanSettingsPayload(
            initialData,
            scanSettingData,
            hasVtKeyEditIntent
        );

        if (isEmptyObject(changedPayload)) {
            setActionShow(false);
            return;
        }

        const vtOnUsed = changedPayload.vt_on ?? initialData.vt_on;
        const vtKeyUsed = changedPayload.vt_key ?? initialData.vt_key;

        if (vtOnUsed && !vtKeyUsed) {
            handleVirusTotalApiKeyFocus();
            setInputErrorMsg(t("pages.scanSettings.scanEngine.api_required"));
            setActionShow(true);
            return;
        }

        if (vtKeyUsed && !isValidToken(vtKeyUsed)) {
            setInputErrorMsg(t("pages.scanSettings.scanEngine.invalid_key"));
            return;
        }

        setIsLoadingFullPage(true);

        updateSetting({
            settings: changedPayload,
            task: UPDATE_SETTINGS_TASKS.UPDATE_SCAN_SETTINGS,
        })
            .then((response) => {
                const nextData = mergeChangedSettings(initialData, changedPayload);

                if (typeof response.data === "object" && response.data.error_msg) {
                    setInputErrorMsg(response.data.error_msg)
                    return
                }

                setInitialData(nextData);
                setSettingData(nextData);
                resetVtKeyInputState(nextData);
                setIsVtKeyFocused(false);
                setHasVtKeyEditIntent(false);
                setInputErrorMsg("");
                if (nextData.vt_key) {
                    setMaskedKey("*".repeat(String(nextData.vt_key).length));
                }
                setActionShow(false);

                setToastValue({
                    type: "success",
                    message: "Saved successfully!",
                    isOpen: true,
                    toastId: Date.now(),
                });
            })
            .catch(() => {
                setToastValue({
                    type: "error",
                    message: "Failed to save settings",
                    isOpen: true,
                    toastId: Date.now(),
                });
            })
            .finally(() => {
                setIsLoadingFullPage(false);
            });
    };

    const handleDiscard = () => {
        setSettingData(initialData);
        resetVtKeyInputState(initialData);
        setIsVtKeyFocused(false);
        setHasVtKeyEditIntent(false);
        setInputErrorMsg("");
        setActionShow(false);
    };

    const handleNoResultChange = (event: SelectChangeEvent<ScanClassification>) => {
        const value = event.target.value as ScanClassification;

        const nextData: IScanSettings = {
            ...scanSettingData,
            no_result: value,
        };

        updateChangedState(nextData);
    };

    const handleErrorChange = (event: SelectChangeEvent<ScanClassification>) => {
        const value = event.target.value as ScanClassification;

        const nextData: IScanSettings = {
            ...scanSettingData,
            on_error: value,
        };

        updateChangedState(nextData);
    };

    const handleVirusTotalEnabledChange = (
        event: React.ChangeEvent<HTMLInputElement>
    ) => {
        const enabled = event.target.checked;

        const nextData: IScanSettings = {
            ...scanSettingData,
            vt_on: enabled,
        };

        updateChangedState(nextData);
    };

    const handleVirusTotalApiKeyFocus = () => {
        if (scanSettingData.vt_on && isUsingStoredVtKey) {
            setIsReplacingVtKey(true);
            setVtKeyInputValue("");
            setIsVtKeyFocused(true);
            setActionShow(true);
        }
    };

    const handleVirusTotalApiKeyBlur = (
        event: React.FocusEvent<HTMLInputElement>
    ) => {
        const isMovingToSave = (event.relatedTarget as HTMLElement | null)
            ?.closest(".save-btn");

        if (!vtKeyInputValue.trim() && scanSettingData.vt_key === initialData.vt_key) {
            if (isMovingToSave) {
                return;
            }

            setIsReplacingVtKey(false);
            setIsVtKeyFocused(false);
            setActionShow(hasChanges);
        }
    };

    const handleVirusTotalApiKeyChange = (
        event: React.ChangeEvent<HTMLInputElement>
    ) => {
        const apiKey = event.target.value.trim();

        setVtKeyInputValue(apiKey);
        setInputErrorMsg("");
        setIsReplacingVtKey(true);
        setHasVtKeyEditIntent(true);

        const nextData: IScanSettings = {
            ...scanSettingData,
            vt_key: apiKey,
        };

        updateChangedState(nextData, true);
    };

    const renderClassificationValue = (selected: unknown) => {
        const selectedOption = categories.find((item) => item.value === selected);
        return selectedOption?.label || "Select a category";
    };

    const saveChangeUI = (
        <Box
            className={"save-change-content"}
            sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                padding: "15px 0",
            }}
        >
            <div className={"btn discard-btn"}>
                <Button variant="outlined" onClick={handleDiscard}>
                    {t("actions.discard")}
                </Button>
            </div>
            <div className={"btn save-btn"}>
                <Button variant="contained" onClick={handleSaveChange}>
                    {t("actions.saveChanges")}
                </Button>
            </div>
        </Box>
    );

    return (
        <div className="scan-settings-page page-content-wrapper">
            <PageHeader
                pageTitle={t("pages.scanSettings.title")}
                infoPage={""}
                breadCrumbData={[
                    {
                        name: "Settings",
                        href: "/settings",
                    },
                    {
                        name: t("pages.scanSettings.title"),
                    },
                ]}
                tooltipStyle={{ minWidth: "650px" }}
                children={<></>}
            />

            <Stack className="page-content-body">
                <div className="scan-settings-content">
                    <SettingBox
                        title={t("pages.scanSettings.scanResult")}
                        titleTooltip={ tooltipEl(t("pages.scanSettings.titleTooltip")) }
                    >
                        <FormGroup
                            aria-label="scan-result"
                            className={"group"}
                            sx={{ maxWidth: "400px" }}
                        >
                            <InputLabel
                                className={"input-label-select"}
                                sx={{ mb: "6px", fontWeight: 600 }}
                            >
                                <div
                                    dangerouslySetInnerHTML={{
                                        __html: t("pages.scanSettings.defaultNoThreat")
                                    }}>
                                </div>
                            </InputLabel>

                            {!isLoading ? (
                                <Select
                                    displayEmpty
                                    inputProps={{ "data-testid": "defaultWhenNoResultSelect" }}
                                    name="no_result"
                                    value={scanSettingData.no_result}
                                    onChange={handleNoResultChange}
                                    renderValue={renderClassificationValue}
                                    className={"group-select-field"}
                                    IconComponent={(props) => <ExpandMoreIcon {...props} />}
                                    size="small"
                                    sx={{ fontWeight: 500 }}
                                >
                                    {categories.map((category) => (
                                        <MenuItem
                                            key={category.value}
                                            value={category.value}
                                            style={{ paddingRight: "12px" }}
                                        >
                                            <Box
                                                sx={{
                                                    width: "100%",
                                                    display: "flex",
                                                    alignItems: "center",
                                                    justifyContent: "space-between",
                                                }}
                                            >
                                                <span>{category.label}</span>
                                                {scanSettingData.no_result === category.value && (
                                                    <CheckIcon className={"checked-icon"} />
                                                )}
                                            </Box>
                                        </MenuItem>
                                    ))}
                                </Select>
                            ) : (
                                <Skeleton animation="wave" sx={{ width: "400px", height: "60px" }} />
                            )}
                        </FormGroup>

                        <FormGroup
                            aria-label="scan-error"
                            sx={{ maxWidth: "400px", mt: 4, mb: 0 }}
                        >
                            <InputLabel
                                className={"input-label-select"}
                                sx={{ mb: "6px", fontWeight: 600 }}
                            >
                                <div
                                    dangerouslySetInnerHTML={{
                                        __html: t("pages.scanSettings.defaultError")
                                    }}>
                                </div>
                            </InputLabel>

                            {!isLoading ? (
                                <Select
                                    displayEmpty
                                    inputProps={{ "data-testid": "defaultWhenErrorSelect" }}
                                    name="on_error"
                                    value={scanSettingData.on_error}
                                    onChange={handleErrorChange}
                                    renderValue={renderClassificationValue}
                                    className={"group-select-field"}
                                    IconComponent={(props) => <ExpandMoreIcon {...props} />}
                                    size="small"
                                    sx={{ fontWeight: 500 }}
                                >
                                    {categories.map((category) => (
                                        <MenuItem
                                            key={category.value}
                                            value={category.value}
                                            style={{ paddingRight: "12px" }}
                                        >
                                            <Box
                                                sx={{
                                                    width: "100%",
                                                    display: "flex",
                                                    alignItems: "center",
                                                    justifyContent: "space-between",
                                                }}
                                            >
                                                <span>{category.label}</span>
                                                {scanSettingData.on_error === category.value && (
                                                    <CheckIcon className={"checked-icon"} />
                                                )}
                                            </Box>
                                        </MenuItem>
                                    ))}
                                </Select>
                            ) : (
                                <Skeleton animation="wave" sx={{ width: "400px", height: "60px" }} />
                            )}
                        </FormGroup>
                    </SettingBox>

                    <SettingBox
                        title={t("pages.scanSettings.scanEngines")}
                        titleTooltip={ tooltipEl(t("pages.scanSettings.scanEngine.titleTooltip")) }
                    >
                        {isLoading ? (
                            <Skeleton
                                animation="wave"
                                height={"150px"}
                                width={"700px"}
                                sx={{ transform: "none", WebkitTransform: "none" }}
                            />
                        ) : (
                            <Box
                                sx={{
                                    padding: 2,
                                    border: "1px solid #E0E0E0",
                                    borderRadius: "4px",
                                    maxWidth: "700px",
                                    justifyContent: "center",
                                }}
                            >
                                <Box>
                                    <span
                                        style={{
                                            fontSize: "16px",
                                            fontWeight: 700,
                                            marginRight: "16px",
                                        }}
                                    >
                                        Virus Total
                                    </span>
                                    <FormControlLabel
                                        className={`virus-total-status-col ${
                                            scanSettingData.vt_on ? "active" : "inactive"
                                        }`}
                                        control={
                                            <GreenSwitch
                                                checked={scanSettingData.vt_on}
                                                onChange={handleVirusTotalEnabledChange}
                                            />
                                        }
                                        label={
                                            <Box>
                                                { t("pages.scanSettings.enableVT") }
                                                {
                                                    tooltipEl(
                                                        t("pages.scanSettings.scanEngine.virusTotal.enableTooltip"),
                                                        { maxWidth: "342px" }
                                                    )
                                                }
                                            </Box>
                                        }
                                    />
                                </Box>

                                {(scanSettingData.vt_on || !!scanSettingData.vt_key) && (
                                    <Box className={"api-key-container"}>
                                        <Divider sx={{ margin: "16px 0" }} />

                                        <FormGroup className={"group"} sx={{ mb: "0" }}>
                                            <InputLabel
                                                className={"input-label-select"}
                                                sx={{ mb: "6px", mr: "6px", fontWeight: 600 }}
                                                required
                                            >
                                                API Key
                                            </InputLabel>

                                            <Box>
                                                <TextField
                                                    type={"text"}
                                                    value={
                                                        isUsingStoredVtKey
                                                            ? maskedKey
                                                            : vtKeyInputValue
                                                    }
                                                    onFocus={handleVirusTotalApiKeyFocus}
                                                    onBlur={handleVirusTotalApiKeyBlur}
                                                    onChange={handleVirusTotalApiKeyChange}
                                                    disabled={!scanSettingData.vt_on}
                                                    placeholder="Input API key"
                                                    size="small"
                                                    error={!!inputErrorMsg}
                                                    fullWidth
                                                    autoComplete="off"
                                                    inputProps={{
                                                        "data-testid": "virusTotalApiKeyInput",
                                                    }}
                                                />
                                                <FormHelperText
                                                    id={"vt_input_error"}
                                                    error
                                                    sx={{
                                                        mt: "6px",
                                                        fontWeight: 500,
                                                        fontSize: "12px",
                                                    }}
                                                >
                                                    {!!inputErrorMsg && inputErrorMsg}
                                                </FormHelperText>
                                            </Box>
                                        </FormGroup>
                                    </Box>
                                )}
                            </Box>
                        )}
                    </SettingBox>
                </div>
            </Stack>

            <Drawer
                variant="persistent"
                anchor="bottom"
                open={isActionShow && (hasChanges || isVtKeyFocused)}
                onClose={() => setActionShow(false)}
                className={"save-change-container"}
            >
                {saveChangeUI}
            </Drawer>

            <Toast
                isOpen={toastValue.isOpen}
                type={toastValue.type}
                message={toastValue.message}
                toastId={toastValue.toastId}
                autoHideDuration={2000}
            />

            <LeavePageDialog
                showDialog={showPrompt}
                confirmNavigation={confirmNavigation}
                cancelNavigation={cancelNavigation}
            />

            <LoadingPage open={isLoadingFullPage} />
        </div>
    );
};

export const ScanSettings = ChangeWithZone(ScanSettingFull);
